import axios from "axios";
import authService from "./authService";
import loggerService from "./loggerService";

const serviceBaseUrlMapping = {
  api: process.env.VUE_APP_API_URL,
};

// Gestionnaire global des requêtes en cours
class RequestManager {
  constructor() {
    this.pendingRequests = new Map();
  }

  addRequest(requestId, controller) {
    this.pendingRequests.set(requestId, controller);
  }

  removeRequest(requestId) {
    this.pendingRequests.delete(requestId);
  }

  cancelAll() {
    loggerService.info(
      `[AXIOS] Annulation de ${this.pendingRequests.size} requête(s) en cours`
    );
    this.pendingRequests.forEach((controller, requestId) => {
      try {
        controller.abort();
        loggerService.info(`[AXIOS] Requête annulée: ${requestId}`);
      } catch (error) {
        loggerService.error(
          `[AXIOS] Erreur lors de l'annulation de la requête ${requestId}:`,
          error
        );
      }
    });
    this.pendingRequests.clear();
  }

  getActiveRequestsCount() {
    return this.pendingRequests.size;
  }
}

export const requestManager = new RequestManager();

export const request = async (
  service,
  method,
  needAuth,
  route,
  query,
  body
) => {
  const requestId = `${method.toUpperCase()}_${route}_${Date.now()}`;
  
  loggerService.info(
    "[AXIOS] (" + service + ") " + method.toUpperCase() + " " + route
  );

  const defaultHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const headers = {
    ...defaultHeaders,
  };

  if (needAuth) {
    const auth = await authService.authenticate();

    if (auth) {
      headers.Authorization = `Token ${auth.token}`;
    } else {
      return null;
    }
  }

  // Créer un AbortController pour cette requête
  const controller = new AbortController();
  requestManager.addRequest(requestId, controller);

  try {
    const res = await axios.request({
      method,
      baseURL: serviceBaseUrlMapping[service],
      url: route,
      headers,
      params: query,
      data: body,
      signal: controller.signal,
      validateStatus: (status) => {
        return status < 500;
      },
    });

    // Retirer la requête de la liste des requêtes en cours
    requestManager.removeRequest(requestId);

    if (res.status === 403 && ["user_not_found"].includes(res.data.code)) {
      authService.removeAuth();
      return null;
    }

    return {
      status: res.status,
      content: res.data,
    };
  } catch (err) {
    // Retirer la requête de la liste des requêtes en cours
    requestManager.removeRequest(requestId);

    // Si la requête a été annulée, on retourne une erreur spécifique
    if (axios.isCancel(err) || err.name === "CanceledError") {
      loggerService.info(`[AXIOS] Requête annulée: ${requestId}`);
      return {
        status: 0,
        cancelled: true,
        content: null,
      };
    }

    if (err.response) {
      return {
        status: err.response.status,
      };
    }

    return {};
  }
};

export default { request, requestManager };
